"use client";

import { useEffect, useState } from 'react';
import { getConsent } from '@/features/consent/utils/consent-api';
import type { TConsentState } from '@/features/consent/types';

export type TPollingPhase = 'idle' | 'polling' | 'signed' | 'timeout';

type Options = {
  enabled: boolean;
  intervalMs?: number;
  timeoutMs?: number;
}

// О подписании бэкенд узнаёт вебхуком, и тот приходит с задержкой в секунды:
// сразу после возврата из Подпислона статус ещё `pending_signature`. Поэтому
// опрашиваем, а не спрашиваем один раз.
export function useConsentPolling({ enabled, intervalMs = 2000, timeoutMs = 30000 }: Options) {
  const [consent, setConsent] = useState<TConsentState | null>(null);
  const [phase, setPhase] = useState<TPollingPhase>('idle');

  useEffect(() => {
    if (!enabled) {
      setPhase('idle');
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const startedAt = Date.now();

    setPhase('polling');

    const tick = async () => {
      try {
        const state = await getConsent();

        if (cancelled) return;

        setConsent(state);

        if (state.allows_upload) {
          setPhase('signed');
          return;
        }
      } catch {
        // Сетевую ошибку провалом не считаем: пробуем ещё раз на следующем шаге.
      }

      if (cancelled) return;

      if (Date.now() - startedAt >= timeoutMs) {
        setPhase('timeout');
        return;
      }

      timer = setTimeout(tick, intervalMs);
    };

    tick();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, intervalMs, timeoutMs]);

  return { consent, phase };
}
